/**
 * 文件仅供preRequest.ts预请求使用，常量和函数默认还是请使用constants.ts/core.ts等别的文件
 */
export const getSearchUrl = () => {
  return window.location.search || window.CsChat?.searchUrl || '';
};

export const getQueryForm = (name: string, searchUrl: string) => {
  const urlSearchParams = new URLSearchParams(searchUrl || window.location.search);

  return urlSearchParams.get(name) || '';
};

type TokenKey = 'chatAccessToken' | 'tokenSuffix' | 'chatAccessTokenRemainTime' | 'chatbotUserId';

type SDKTokens = {
  [key in TokenKey]?: string;
};
export function getSDKToken() {
  const tokenInLocalStorage = window.localStorage.getItem('live_chat_sdk_token') || '{}';
  let tokens: SDKTokens = {};

  try {
    tokens = tokens ? JSON.parse(tokenInLocalStorage) : {};
  } catch (e) {
    tokens = {};
  }

  return tokens;
}

export function getLiveChatSDKHttpHeaders() {
  const tokens = getSDKToken();

  if (ENVIRONMENT !== 'live') {
    // 方便在 Web 端测试，直接使用 URL 参数中的 token
    const chatAccessToken = getQueryForm('chatAccessToken', window.location.search);
    return {
      'chat-access-token': chatAccessToken.length === 0 ? tokens.chatAccessToken || '' : chatAccessToken,
      'token-suffix': tokens.tokenSuffix || '',
    };
  }

  return {
    'chat-access-token': tokens.chatAccessToken || '',
    'token-suffix': tokens.tokenSuffix || '',
  };
}

export const API_CONFIG = {
  getComposedInitSession: {
    name: 'getComposedInitSession',
    baseUrl: '/api/v2',
    url: '/composed_init_session',
    method: 'get',
  },
  getFirstInitSession: {
    name: 'getFirstInitSession',
    baseUrl: '/api/v2',
    url: '/init_session_v1',
    method: 'get',
  },
  getInitSession: {
    name: 'getInitSession',
    baseUrl: '/api/v2',
    url: '/init_session',
    method: 'get',
  },
  getExperiment: {
    name: 'getExperiment',
    url: '/experiment/all',
    baseUrl: '/api/v2',
    method: 'get',
  },
  virtualCallStatusCheck: {
    name: 'virtualCallStatusCheck',
    url: '/live_agent_connection/status_check',
    baseUrl: '/api/v2',
    method: 'get',
  },
};

export enum CHATBOT_TYPE {
  /** 普通用户 走chatbot */
  NORMAL_CHATBOT = 1,
  /** 高级用户 走chatbot */
  PREMIUM_CHATBOT,
  /** 高级用户 走live chat */
  PREMIUM_LIVE_CHAT,
}

export enum ENTRY_POINT {
  general,
  mePage,
  sellingPage,
  shippingPage,
  helpCentre,
  sellerCenterDesktop,
  sellerEducationHub,
  wmsLite,
  CNSellerCenterEntryPointNumber,
  CNEducationHubEntryPointNumber,
  CNEnterpriseWechatEntryPointNumber,
  mitra = 13,
  shopeeFoodHC = 14,
  cnsc = 15,
  BankHelpCenter = 19,
  BankMePage = 20,
  driver = 16,
  DriverAPPFoodOrderPage = 16,
  DriverAPPHelpCenter = 17,
  DriverAPPSPXOrderPage = 18,
  MitraPartner = 27,
  ShopeeFoodPartner = 28,
  ShopeePayPartner = 29,
  MystockPartner = 30,
  RRDetailsPage = 31,
  RRShippingPage = 32,
  DriverAPPOngoingOrderPage = 39,
  DriverAPPOrderDetailsPage = 40,
  DriverAPPOrderHistoryPage = 37,
  DriverAPPReportIssuePage = 38,
  OrderDetailPage = 41,
  ShopeeAPPFoodVNHC = 42,
  ShopeeFoodVNAppHC = 43,
  ShopeeFoodVNDriverHC = 44,
  ShopeeHomePage = 47,
  ShopeeHomePageNologin = 51,

  // premium user
  mePagePremium = 100,
  helpCentrePremium = 101,
  sellingPagePremium = 102,
  shippingPagePremium = 103,
  RRDetailsPagePremium = 104,
  RRShippingPagePremium = 105,
  OrderDetailPagePremium = 141,

  ShopeeLiteGeneral = 200,
  ShopeeLiteMePage = 201,
  ShopeeLiteHelpCentre = 202,
  ShopeeLiteShippingPage = 203,
  ShopeeLiteRRDetailsPage = 204,
  ShopeeLiteRRShippingPage = 205,
  BankSGMePage = 45,
  BankSGHelpCenter = 46,
  BankIDMePage = 35,
  BankIDHelpCenter = 36,
}

export const isDriverSupportChatbot = () => {
  const searchUrl = getSearchUrl();
  return (
    getQueryForm('toLiveChat', searchUrl) === DirectlyToLiveChat.No
    && !!getQueryForm('currentDeliveryId', searchUrl)
    && !!getQueryForm('deliveryId', searchUrl)
  );
};

export enum App {
  Shopee,
  Mitra,
  Driver,
  Partner,
  Bank,
  ShopeeLite,
  Food,
}

export enum ShopeeEntryPoint {
  general,
  mePage,
  sellingPage,
  shippingPage,
  helpCentre,
  shopeeFoodHC = 14,
  RRDetailsPage = 31,
  RRShippingPage = 32,
  OrderDetailPage = 41,
  ShopeeAPPFoodVNHC = 42,
  // premium user
  mePagePremium = 100,
  helpCentrePremium = 101,
  RRDetailsPagePremium = 104,
  RRShippingPagePremium = 105,
  OrderDetailPagePremium = 141,

  ShopeeLiteGeneral = 200,
  ShopeeLiteMePage = 201,
  ShopeeLiteHelpCentre = 202,
  ShopeeLiteShippingPage = 203,
  ShopeeLiteRRDetailsPage = 204,
  ShopeeLiteRRShippingPage = 205,
}

export enum MitraEntryPoint {
  mitra = 13,
}

export enum DriverEntryPoint {
  driver = 16,
  DriverAPPFoodOrderPage = 16,
  DriverAPPHelpCenter = 17,
  DriverAPPSPXOrderPage = 18,
  DriverAPPOngoingOrderPage = 39,
  DriverAPPOrderDetailsPage = 40,
  DriverAPPOrderHistoryPage = 37,
  DriverAPPReportIssuePage = 38,
  ShopeeFoodVNDriverHC = 44,
}

export enum PartnerEntryPoint {
  MitraPartner = 27,
  ShopeeFoodPartner = 28,
  ShopeePayPartner = 29,
  MystockPartner = 30,
}

export enum BankEntryPoint {
  BankHelpCenter = 19,
  BankMePage = 20,
  BankIDMePage = 35,
  BankIDHelpCenter = 36,
}

export enum FoodEntryPoint {
  ShopeeFoodVNAppHC = 43,
}

// 是否直接进线
export enum DirectlyToLiveChat {
  No = '0',
  Yes = '1',
}

export enum ShopeeLiteEntryPoint {
  ShopeeLiteGeneral = 200,
  ShopeeLiteMePage = 201,
  ShopeeLiteHelpCentre = 202,
  ShopeeLiteShippingPage = 203,
  ShopeeLiteRRDetailsPage = 204,
  ShopeeLiteRRShippingPage = 205,
}

export type AppConfigType = {
  [key in App]: AppConfigItem[];
};

export interface AppConfigItem {
  app: App; // 所属app
  fromInUrl:
  | keyof typeof ShopeeEntryPoint
  | keyof typeof MitraEntryPoint
  | keyof typeof DriverEntryPoint
  | keyof typeof PartnerEntryPoint
  | keyof typeof BankEntryPoint
  | keyof typeof ShopeeLiteEntryPoint
  | keyof typeof FoodEntryPoint; // from参数
  hasChatbotCountries: string[]; // 有chatbot的地区
  isSupportChatbot?: boolean; // 是否支持chatbot
  entryPoint:
  | ShopeeEntryPoint
  | MitraEntryPoint
  | DriverEntryPoint
  | PartnerEntryPoint
  | BankEntryPoint
  | ShopeeLiteEntryPoint
  | FoodEntryPoint; // 入口
}

export const defaultAppConfig: AppConfigItem = {
  app: App.Shopee,
  fromInUrl: 'general',
  isSupportChatbot: true,
  hasChatbotCountries: Object.values(process.env.COUNTRIES as unknown as { [key: string]: string }),
  entryPoint: 0,
};
export const getConfigByEntryPoint = (entryPoint: number) => {
  switch (entryPoint) {
    case ENTRY_POINT.ShopeeAPPFoodVNHC:
    case ENTRY_POINT.ShopeeFoodVNAppHC:
    case ENTRY_POINT.ShopeeFoodVNDriverHC:
      return { isSupportChatbot: false };
    default:
      return {};
  }
};
// 简化只提供app/fromInUrl/isSupportChatbot三个属性，因为preRequest只需要这三个
export const AppConfig: AppConfigType = {
  [App.Shopee]: Object.keys(ShopeeEntryPoint).map((fromInUrl: string) => ({
    ...defaultAppConfig,
    fromInUrl: fromInUrl as keyof typeof ShopeeEntryPoint,
    entryPoint: ShopeeEntryPoint[fromInUrl as keyof typeof ShopeeEntryPoint],
    ...getConfigByEntryPoint(ShopeeEntryPoint[fromInUrl as keyof typeof ShopeeEntryPoint]),
  })),
  [App.Mitra]: Object.keys(MitraEntryPoint).map((fromInUrl: string) => ({
    app: App.Mitra,
    fromInUrl: fromInUrl as keyof typeof MitraEntryPoint,
    hasChatbotCountries: [],
    isSupportChatbot: false,
    entryPoint: MitraEntryPoint[fromInUrl as keyof typeof MitraEntryPoint],
  })),
  [App.Driver]: Object.keys(DriverEntryPoint).map((fromInUrl: string) => ({
    app: App.Driver,
    fromInUrl: fromInUrl as keyof typeof DriverEntryPoint,
    hasChatbotCountries: Object.values(process.env.COUNTRIES as unknown as { [key: string]: string }),
    isSupportChatbot: isDriverSupportChatbot(),
    entryPoint: DriverEntryPoint[fromInUrl as keyof typeof DriverEntryPoint],
    ...getConfigByEntryPoint(DriverEntryPoint[fromInUrl as keyof typeof DriverEntryPoint]),
  })),
  [App.Partner]: Object.keys(PartnerEntryPoint).map((fromInUrl: string) => ({
    app: App.Partner,
    fromInUrl: fromInUrl as keyof typeof PartnerEntryPoint,
    hasChatbotCountries: [],
    isSupportChatbot: false,
    entryPoint: PartnerEntryPoint[fromInUrl as keyof typeof PartnerEntryPoint],
  })),
  [App.Bank]: Object.keys(BankEntryPoint).map((fromInUrl: string) => ({
    app: App.Bank,
    fromInUrl: fromInUrl as keyof typeof BankEntryPoint,
    hasChatbotCountries: ['id', 'ph'],
    isSupportChatbot: true,
    entryPoint: BankEntryPoint[fromInUrl as keyof typeof BankEntryPoint],
  })),
  [App.ShopeeLite]: Object.keys(ShopeeLiteEntryPoint).map((fromInUrl: string) => ({
    ...defaultAppConfig,
    app: App.ShopeeLite,
    entryPoint: ShopeeLiteEntryPoint[fromInUrl as keyof typeof ShopeeLiteEntryPoint],
    fromInUrl: fromInUrl as keyof typeof ShopeeLiteEntryPoint,
  })),
  [App.Food]: Object.keys(FoodEntryPoint).map((fromInUrl: string) => ({
    app: App.Food,
    fromInUrl: fromInUrl as keyof typeof FoodEntryPoint,
    hasChatbotCountries: [],
    isSupportChatbot: false,
    entryPoint: FoodEntryPoint[fromInUrl as keyof typeof FoodEntryPoint],
    ...getConfigByEntryPoint(FoodEntryPoint[fromInUrl as keyof typeof FoodEntryPoint]),
  })),
};

export declare enum UserSessionChannelType {
  ChatbotChannel = 1,
  SellerCenterChannel = 2,
  SellerEducationHubChannel = 3,
  WMSLiteChannel = 4,
  PremiumUserChannel = 5,
  ShopCNChannel = 6,
  SpxChannel = 7,
}

export declare enum UserSessionStatus {
  initial = 10,
  created = 20,
  queuing = 30,
  chatting = 40,
  ended = 50,
}

export const ACTUAL_ENTRY_POINT = {
  [ENTRY_POINT.mePagePremium]: ENTRY_POINT.mePage,
  [ENTRY_POINT.helpCentrePremium]: ENTRY_POINT.helpCentre,
  [ENTRY_POINT.RRDetailsPagePremium]: ENTRY_POINT.RRDetailsPage,
  [ENTRY_POINT.RRShippingPagePremium]: ENTRY_POINT.RRShippingPage,
};

export const ClientType = {
  App: 1,
  Web: 2,
};

export const PREMIUM_USER_ENTRY_POINT = [
  ENTRY_POINT.mePagePremium,
  ENTRY_POINT.helpCentrePremium,
  ENTRY_POINT.RRDetailsPagePremium,
  ENTRY_POINT.RRShippingPagePremium,
];

export enum ENTRY_BUTTON_TYPE {
  NORMAL_BUTTON = 1,
  PREMIUM_BUTTON,
}

// SPCB-30284 需求 rules base 支持 case
const CaseOptionMatch = '30284-CaseOptionMatch';
const OtherFrequentlyAsked = '31864-OtherFrequentlyAsked';

// 需要做发布顺序兼容 or 版本控制的才加进来
// key 的拼接规则统一为：{jira单号}-{feature简短的描述}
export const PreRequestSupportFeatures = [CaseOptionMatch, OtherFrequentlyAsked];
